import ProductStatusNotice from './ProductStatusNotice';
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Printer, LifeBuoy } from "lucide-react";
import { policies } from "@/data/policyRegistry";
import { SITE } from "@/lib/site";
import useSeo from "@/hooks/useSeo";
import PolicyBody, { slugify } from "@/components/site/PolicyBody";
import Photo from "@/components/site/Photo";

export default function PolicyView({ policy }) {
  const [active, setActive] = useState(null);
  const sections = policy.sections.filter((s) => s.heading);

  useSeo({ title: `${policy.title} — Orrbbit`, description: policy.description, path: policy.route });

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); }),
      { rootMargin: "-25% 0px -65% 0px" }
    );
    sections.forEach((s) => {
      const el = document.getElementById(slugify(s.heading));
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [policy]);

  const related = policies.filter((p) => p.category === policy.category && p.slug !== policy.slug).slice(0, 4);

  return (
    <div className="bg-white" data-testid="policy-page">
      <div className="border-b border-border bg-lightbg">
        <div className="container-x py-10 md:py-14">
          <Link to="/policies" data-testid="policy-back-link" className="inline-flex items-center gap-1 text-sm font-medium text-bodygrey hover:text-teal">
            <ChevronLeft size={16} /> All policies
          </Link>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
            <span className="rounded-full bg-teal/10 px-3 py-1 font-semibold text-teal">{policy.category}</span>
            <span className="rounded-full bg-white px-3 py-1 font-medium text-bodygrey ring-1 ring-border">Version {policy.version}</span>
          </div>
          <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight text-navy md:text-5xl">{policy.title}</h1>
          <p className="mt-3 max-w-2xl text-bodygrey">{policy.description}</p>
          <div className="mt-5 flex flex-wrap gap-x-6 gap-y-1 text-sm text-bodygrey">
            <span>Effective date: <strong className="text-navy">{policy.effective_date}</strong></span>
            <span>Last updated: <strong className="text-navy">{policy.last_updated}</strong></span>
          </div>
          <button onClick={() => window.print()} data-testid="policy-print-btn" className="no-print mt-5 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-navy ring-1 ring-border hover:bg-lightbg">
            <Printer size={15} /> Print
          </button>
        </div>
      </div>

      {policy.image && (
        <div className="container-x pt-10 md:pt-14">
          <Photo slug={policy.image} ratio="aspect-[21/9]" priority className="shadow-soft" data-testid="policy-hero-photo" />
        </div>
      )}

      <div className="container-x py-10 md:py-14">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
          <aside className="no-print lg:col-span-3">
            <div className="lg:sticky lg:top-24">
              <details className="rounded-xl border border-border bg-lightbg p-4 lg:border-0 lg:bg-transparent lg:p-0" open>
                <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wider text-bodygrey lg:list-none">On this page</summary>
                <nav className="mt-3 flex flex-col gap-1 border-l border-border">
                  {sections.map((s) => {
                    const id = slugify(s.heading);
                    return (
                      <a key={id} href={`#${id}`} data-testid={`toc-${id}`}
                        className={`-ml-px border-l-2 py-1.5 pl-3 text-sm transition-colors ${active === id ? "border-teal font-medium text-navy" : "border-transparent text-bodygrey hover:text-navy"}`}>
                        {s.heading}
                      </a>
                    );
                  })}
                </nav>
              </details>
            </div>
          </aside>

          <article className="lg:col-span-8 lg:col-start-4">
            <div className="max-w-3xl">
              <ProductStatusNotice/><PolicyBody policy={policy} />
              <div className="mt-10 rounded-2xl border border-border bg-lightbg p-6">
                <div className="flex items-center gap-2 text-navy"><LifeBuoy size={18} className="text-teal" /><span className="font-semibold">Questions about this policy?</span></div>
                <p className="mt-2 text-sm text-bodygrey">Contact our team and we'll be happy to help.</p>
                <a href={`mailto:${SITE.supportEmail}`} data-testid="policy-contact-email" className="mt-3 inline-block rounded-full bg-teal px-5 py-2 text-sm font-semibold text-white transition-transform hover:scale-105">{SITE.supportEmail}</a>
              </div>
              {related.length > 0 && (
                <div className="no-print mt-10">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-bodygrey">Related policies</h3>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {related.map((r) => (
                      <Link key={r.slug} to={r.route} className="rounded-full border border-border bg-white px-4 py-2 text-sm text-navy hover:border-teal hover:text-teal">{r.title}</Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </article>
        </div>
      </div>
    </div>
  );
}

